/**
 * Pure timing math for the single timeline music track. Shared by the export
 * mixer (OfflineAudioContext schedule), the preview <audio> sync and the
 * timeline lane, so all three agree on where the music plays and how loud.
 *
 * Model: the trimmed source range [trimStartMs, trimEndMs) plays from
 * timeline `offsetMs`, cut off at the end of the timeline. Fades apply to
 * that audible window, so a track longer than the video fades out at the
 * video's end rather than being chopped.
 */

import type { AudioTrack } from '@canvas/shared-types';

export const DEFAULT_AUDIO_VOLUME = 0.8;
export const DEFAULT_FADE_OUT_MS = 1000;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createAudioTrack(args: {
  trackId: string;
  name: string;
  source: AudioTrack['source'];
  mimeType: string;
  durationMs: number;
  storagePath?: string;
  url?: string;
  idbKey?: string;
}): AudioTrack {
  return clampAudioTrack({
    v: 1,
    ...args,
    offsetMs: 0,
    trimStartMs: 0,
    trimEndMs: args.durationMs,
    volume: DEFAULT_AUDIO_VOLUME,
    fade: { inMs: 0, outMs: DEFAULT_FADE_OUT_MS },
  });
}

/** Keep every field inside its legal range. */
export function clampAudioTrack(track: AudioTrack): AudioTrack {
  const duration = Math.max(0, track.durationMs);
  const trimStartMs = Math.round(clamp(track.trimStartMs, 0, Math.max(0, duration - 1)));
  const trimEndMs = Math.round(clamp(track.trimEndMs || duration, trimStartMs + 1, Math.max(trimStartMs + 1, duration)));
  const length = trimEndMs - trimStartMs;
  const half = length / 2;
  return {
    ...track,
    offsetMs: Math.round(Math.max(0, track.offsetMs)),
    trimStartMs,
    trimEndMs,
    volume: clamp(Number.isFinite(track.volume) ? track.volume : DEFAULT_AUDIO_VOLUME, 0, 1),
    fade: {
      inMs: Math.round(clamp(track.fade?.inMs ?? 0, 0, half)),
      outMs: Math.round(clamp(track.fade?.outMs ?? 0, 0, half)),
    },
  };
}

export interface AudioWindow {
  /** Timeline ms where the music starts. */
  startMs: number;
  /** Timeline ms where it stops (timeline end or source end, whichever first). */
  endMs: number;
  /** Source ms played at `startMs`. */
  sourceStartMs: number;
}

/** The audible window on a timeline of `totalMs`, or null when nothing plays. */
export function audioWindow(track: AudioTrack, totalMs: number): AudioWindow | null {
  const startMs = track.offsetMs;
  const endMs = Math.min(totalMs, startMs + (track.trimEndMs - track.trimStartMs));
  if (endMs <= startMs) return null;
  return { startMs, endMs, sourceStartMs: track.trimStartMs };
}

/** Source position at timeline `ms`, or null outside the window. */
export function sourceMsAt(track: AudioTrack, totalMs: number, ms: number): number | null {
  const w = audioWindow(track, totalMs);
  if (!w || ms < w.startMs || ms >= w.endMs) return null;
  return w.sourceStartMs + (ms - w.startMs);
}

/** Fade ramps (0..1) at timeline `ms`, excluding the track volume. */
export function fadeAt(track: AudioTrack, totalMs: number, ms: number): number {
  const w = audioWindow(track, totalMs);
  if (!w || ms < w.startMs || ms >= w.endMs) return 0;
  const { inMs, outMs } = effectiveFades(track, w);
  const fin = inMs > 0 ? clamp((ms - w.startMs) / inMs, 0, 1) : 1;
  const fout = outMs > 0 ? clamp((w.endMs - ms) / outMs, 0, 1) : 1;
  return Math.min(fin, fout);
}

/** Output gain (volume × fades) at timeline `ms`. */
export function gainAt(track: AudioTrack, totalMs: number, ms: number): number {
  return track.volume * fadeAt(track, totalMs, ms);
}

/** Fades limited to half the AUDIBLE window, which the timeline end may have shortened. */
export function effectiveFades(track: AudioTrack, w: AudioWindow): { inMs: number; outMs: number } {
  const half = (w.endMs - w.startMs) / 2;
  return { inMs: Math.min(track.fade.inMs, half), outMs: Math.min(track.fade.outMs, half) };
}
